import React, { useEffect, useRef, useState, useCallback } from 'react';
import { HaltDetail, getAmbulanceLocationHistory } from '../../services/api';

/* ─── Custom overlay (speech-bubble info window) ─── */
type CustomOverlayInstance = {
  setPosition(latlng: any): void;
  setContent(html: string): void;
  setMap(map: any): void;
};

function createCustomInfoOverlay(map: any, position: any, content: string): CustomOverlayInstance {
  const OverlayView = (window as any).google?.maps?.OverlayView;
  if (!OverlayView) return { setPosition: () => {}, setContent: () => {}, setMap: () => {} };

  function Overlay(this: any, pos: any, c: string) {
    OverlayView.call(this);
    this.position = pos;
    this.content = c;
    this.div = null;
  }

  Overlay.prototype = Object.create(OverlayView.prototype);
  Overlay.prototype.onAdd = function (this: any) {
    this.div = document.createElement('div');
    this.div.style.cssText = 'position:absolute;pointer-events:auto;z-index:100;display:flex;flex-direction:column;align-items:center;';
    this.div.innerHTML = this.content;
    // Close button handler
    this.div.addEventListener('click', (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('custom-iw-close')) {
        this.setMap(null);
      }
    });
    const panes = this.getPanes();
    if (panes?.floatPane) panes.floatPane.appendChild(this.div);
  };
  Overlay.prototype.draw = function (this: any) {
    if (!this.div || !this.position) return;
    const projection = this.getProjection();
    if (!projection) return;
    const point = projection.fromLatLngToDivPixel(this.position);
    if (point) {
      const divW = this.div.offsetWidth || 0;
      const divH = this.div.offsetHeight || 0;
      this.div.style.left = `${point.x - divW / 2}px`;
      this.div.style.top = `${point.y - divH - 30}px`;
    }
  };
  Overlay.prototype.onRemove = function (this: any) {
    if (this.div?.parentNode) this.div.parentNode.removeChild(this.div);
    this.div = null;
  };
  Overlay.prototype.setPosition = function (this: any, latlng: any) {
    this.position = latlng;
    this.draw();
  };
  Overlay.prototype.setContent = function (this: any, html: string) {
    this.content = html;
    if (this.div) this.div.innerHTML = html;
  };

  const overlay = new (Overlay as any)(position, content);
  overlay.setMap(map);
  return overlay;
}

/* ─── Helpers ─── */
function buildInfoContent(
  title: string,
  titleColor: string,
  lines: string[],
  isDark: boolean,
): string {
  const textColor = isDark ? '#f1f5f9' : '#0f172a';
  const subColor = isDark ? '#94a3b8' : '#64748b';
  const bgColor = isDark ? '#1e293b' : '#ffffff';
  const borderColor = isDark ? '#334155' : '#e2e8f0';
  return (
    '<div style="display:flex;flex-direction:column;align-items:center;">' +
    '<div style="color:' + textColor + ';background:' + bgColor + ';border:1px solid ' + borderColor + ';font-size:13px;width:max-content;max-width:260px;padding:10px 14px;border-radius:8px;box-shadow:0 2px 12px rgba(0,0,0,0.15);position:relative;">' +
    '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">' +
    '<strong style="color:' + titleColor + ';font-size:13px;">' + title + '</strong>' +
    '<span class="custom-iw-close" style="cursor:pointer;font-size:18px;line-height:1;color:' + subColor + ';margin-left:12px;">&times;</span>' +
    '</div>' +
    lines.map((l) => '<div style="color:' + subColor + ';font-size:12px;line-height:1.4;">' + l + '</div>').join('') +
    '</div>' +
    '<div style="width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:8px solid ' + bgColor + ';margin-top:-1px;"></div>' +
    '</div>'
  );
}

/* ─── Props ─── */
interface HaltDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  vehicleNo: string;
  licensePlate: string | null;
  deviceImei: string | null;
  haltDetails: HaltDetail[];
  selectedDate: string;
  isDarkTheme: boolean;
}

interface PathPoint {
  lat: number;
  lng: number;
}

const START_MARKER_URL = '/images/start_marker.png';
const END_MARKER_URL = '/images/end_marker.png';

const HaltDetailDrawer: React.FC<HaltDetailDrawerProps> = ({
  open,
  onClose,
  vehicleNo,
  licensePlate,
  deviceImei,
  haltDetails,
  selectedDate,
  isDarkTheme,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const polylineRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const overlaysRef = useRef<CustomOverlayInstance[]>([]);
  const activeOverlayRef = useRef<CustomOverlayInstance | null>(null);

  const [pathData, setPathData] = useState<PathPoint[]>([]);
  const [loadingPath, setLoadingPath] = useState(false);
  const [pathError, setPathError] = useState('');
  const [highlightedHalt, setHighlightedHalt] = useState<number | null>(null);

  const formatTime = (isoString: string): string => {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '—';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  };

  // ESC key to close drawer
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && typeof activeElement.blur === 'function') {
          activeElement.blur();
        }
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (open) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  const fetchPath = useCallback(async () => {
    if (!deviceImei || !selectedDate) {
      setPathData([]);
      return;
    }
    setLoadingPath(true);
    setPathError('');
    try {
      const response = await getAmbulanceLocationHistory(deviceImei, selectedDate);
      const history = response.data?.history || [];
      const points: PathPoint[] = history
        .filter((p: any) => p.date)
        .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
        .map((p: any) => {
          if (p.location && Array.isArray(p.location)) {
            return { lat: p.location[1], lng: p.location[0] };
          } else if (p.latitude !== undefined && p.longitude !== undefined) {
            return { lat: p.latitude, lng: p.longitude };
          }
          return null;
        })
        .filter((p: PathPoint | null): p is PathPoint => p !== null && !isNaN(p.lat) && !isNaN(p.lng));
      setPathData(points);
    } catch (err: any) {
      console.error('Error fetching path data:', err);
      setPathError('Failed to load vehicle path');
      setPathData([]);
    } finally {
      setLoadingPath(false);
    }
  }, [deviceImei, selectedDate]);

  useEffect(() => {
    if (open) {
      fetchPath();
    } else {
      setPathData([]);
      setPathError('');
      setHighlightedHalt(null);
    }
  }, [open, fetchPath]);

  const clearMapOverlays = useCallback(() => {
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    overlaysRef.current.forEach((o) => o.setMap(null));
    overlaysRef.current = [];
    activeOverlayRef.current = null;
  }, []);

  useEffect(() => {
    if (!open || !mapRef.current || !window.google?.maps) return;

    if (!mapInstanceRef.current) {
      const defaultCenter = pathData.length > 0
        ? { lat: pathData[0].lat, lng: pathData[0].lng }
        : haltDetails.length > 0
          ? { lat: haltDetails[0].location.lat, lng: haltDetails[0].location.lng }
          : { lat: 20.5937, lng: 78.9629 };

      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: new window.google.maps.LatLng(defaultCenter.lat, defaultCenter.lng),
        zoom: 13,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        fullscreenControl: true,
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: true,
      } as any);
    }

    const map = mapInstanceRef.current;
    clearMapOverlays();

    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;

    const showOverlay = (overlay: CustomOverlayInstance) => {
      if (activeOverlayRef.current && activeOverlayRef.current !== overlay) {
        activeOverlayRef.current.setMap(null);
      }
      overlay.setMap(map);
      activeOverlayRef.current = overlay;
    };

    // Draw polyline path
    if (pathData.length >= 2) {
      const pathCoords = pathData.map((p) => new window.google.maps.LatLng(p.lat, p.lng));
      pathCoords.forEach((c: any) => { bounds.extend(c); hasPoints = true; });

      const polyline = new window.google.maps.Polyline({
        path: pathCoords,
        geodesic: true,
        strokeColor: '#3b82f6',
        strokeOpacity: 0.8,
        strokeWeight: 3,
        map,
      });
      polylineRef.current = polyline;

      // Start marker
      const startIcon: google.maps.Icon = {
        url: START_MARKER_URL,
        scaledSize: new google.maps.Size(48, 48),
        anchor: new google.maps.Point(27, 45),
      };
      const startMarker = new google.maps.Marker({
        position: pathCoords[0],
        map,
        icon: startIcon,
        title: 'Start',
      });
      const startOverlay = createCustomInfoOverlay(
        null,
        pathCoords[0],
        buildInfoContent('Start Point', '#16a34a', [
          `${pathData[0].lat.toFixed(5)}, ${pathData[0].lng.toFixed(5)}`,
        ], isDarkTheme),
      );
      startOverlay.setMap(null);
      startMarker.addListener('click', () => showOverlay(startOverlay));
      markersRef.current.push(startMarker);
      overlaysRef.current.push(startOverlay);

      // End marker
      const endIcon: google.maps.Icon = {
        url: END_MARKER_URL,
        scaledSize: new google.maps.Size(48, 48),
        anchor: new google.maps.Point(27, 45),
      };
      const endMarker = new google.maps.Marker({
        position: pathCoords[pathCoords.length - 1],
        map,
        icon: endIcon,
        title: 'End',
      });
      const endOverlay = createCustomInfoOverlay(
        null,
        pathCoords[pathCoords.length - 1],
        buildInfoContent('End Point', '#dc2626', [
          `${pathData[pathData.length - 1].lat.toFixed(5)}, ${pathData[pathData.length - 1].lng.toFixed(5)}`,
        ], isDarkTheme),
      );
      endOverlay.setMap(null);
      endMarker.addListener('click', () => showOverlay(endOverlay));
      markersRef.current.push(endMarker);
      overlaysRef.current.push(endOverlay);
    }

    // Halt markers (orange circles with number labels)
    haltDetails.forEach((halt, idx) => {
      const pos = new window.google.maps.LatLng(halt.location.lat, halt.location.lng);
      bounds.extend(pos);
      hasPoints = true;

      const isHighlighted = highlightedHalt === idx;

      const haltMarker = new window.google.maps.Marker({
        position: pos,
        map,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: isHighlighted ? '#f59e0b' : '#f97316',
          fillOpacity: 1,
          strokeColor: isHighlighted ? '#f59e0b' : '#ffffff',
          strokeWeight: isHighlighted ? 3 : 2,
          scale: isHighlighted ? 12 : 8,
        },
        title: `Halt ${idx + 1}`,
        label: {
          text: String(idx + 1),
          color: '#ffffff',
          fontSize: '10px',
          fontWeight: '700',
        },
      } as any);

      const addressLine = halt.address && halt.address !== '—' ? halt.address : '';
      const lines = [
        `Duration: ${halt.durationFormatted}`,
        `${formatTime(halt.startTime)} → ${formatTime(halt.endTime)}`,
      ];
      if (addressLine) lines.push(addressLine);
      lines.push(`${halt.location.lat.toFixed(5)}, ${halt.location.lng.toFixed(5)}`);

      const haltOverlay = createCustomInfoOverlay(
        null,
        pos,
        buildInfoContent(`Halt ${idx + 1}`, '#ea580c', lines, isDarkTheme),
      );
      haltOverlay.setMap(null);
      haltMarker.addListener('click', () => showOverlay(haltOverlay));
      markersRef.current.push(haltMarker);
      overlaysRef.current.push(haltOverlay);
    });

    if (hasPoints) {
      map.fitBounds(bounds);
      const listener = window.google.maps.event.addListenerOnce(map, 'idle', () => {
        const currentZoom = map.getZoom();
        if (currentZoom && currentZoom > 16) map.setZoom(16);
      });
      return () => {
        if (listener) window.google.maps.event.removeListener(listener);
      };
    }
  }, [open, pathData, haltDetails, isDarkTheme, highlightedHalt, clearMapOverlays]);

  useEffect(() => {
    if (!open) {
      clearMapOverlays();
      mapInstanceRef.current = null;
    }
  }, [open, clearMapOverlays]);

  const handleHaltHover = (idx: number | null) => {
    setHighlightedHalt(idx);
    if (idx !== null && mapInstanceRef.current && haltDetails[idx]) {
      const halt = haltDetails[idx];
      mapInstanceRef.current.panTo(new window.google.maps.LatLng(halt.location.lat, halt.location.lng));
    }
  };

  if (!open) return null;

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', maxWidth: '800px', minWidth: '690px' }}>
        {/* Header */}
        <div className="drawer-header">
          <div>
            <h3 className="drawer-title" style={{ margin: 0 }}>{vehicleNo} ({licensePlate})</h3>
          </div>
          <button className="drawer-close" onClick={onClose}>&times;</button>
        </div>

        {/* Content: halt details (top, max 50%) + map (bottom, min 50%) */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 , padding: 10}}>

          {/* Halt details — top, max 50% */}
          <div style={{
            maxHeight: '50%',
            flexShrink: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}>
            {/* Section title — fixed, not scrollable */}
            <div style={{
              padding: '10px 0px 6px',
              fontSize: '14px',
              fontWeight: 600,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '18px',
              flexShrink: 0,
            }}>
              <span style={{ color: isDarkTheme ? '#e2e8f0' : '#1e293b' }}>Halt Details</span>
              <span style={{ fontSize: '13px', color: isDarkTheme ? '#94a3b8' : '#64748b' }}>
                {haltDetails.length} halt{haltDetails.length !== 1 ? 's' : ''}
              </span>
              <span style={{ fontSize: '13px', color: isDarkTheme ? '#94a3b8' : '#64748b' }}>
                {selectedDate}
              </span>
            </div>

            {haltDetails.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '24px 20px',
                fontSize: '14px',
                color: isDarkTheme ? '#64748b' : '#94a3b8',
              }}>
                No halts detected for this vehicle
              </div>
            ) : (
              <>
                {/* Table header — fixed, not scrollable */}
                <table className="bookings-table" style={{ width: '100%', borderCollapse: 'collapse', flexShrink: 0, tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '48px' }} />
                    <col style={{ width: '35%' }} />
                    <col style={{ width: '20%' }} />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      {['#', 'Time', 'Duration', 'Location'].map((h) => (
                        <th key={h} style={{
                          padding: '6px 12px',
                          textAlign: 'left',
                          fontWeight: 600,
                          fontSize: '12px',
                          textTransform: 'uppercase' as const,
                          letterSpacing: '0.5px',
                          whiteSpace: 'nowrap' as const,
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                </table>

                {/* Table body — scrollable */}
                <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                  <table className="bookings-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '48px' }} />
                      <col style={{ width: '35%' }} />
                      <col style={{ width: '20%' }} />
                      <col />
                    </colgroup>
                    <tbody>
                      {haltDetails.map((halt, idx) => (
                        <tr
                          key={idx}
                          onMouseEnter={() => handleHaltHover(idx)}
                          onMouseLeave={() => handleHaltHover(null)}
                          style={{
                            cursor: 'pointer',
                            transition: 'background-color 0.15s ease',
                            backgroundColor: highlightedHalt === idx
                              ? (isDarkTheme ? '#1e3a5f' : '#eff6ff')
                              : 'transparent',
                          }}
                        >
                          <td style={{ padding: '6px 12px', fontSize: '13px' }}>
                            <span style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              backgroundColor: '#f97316',
                              color: '#fff',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              fontWeight: 700,
                            }}>
                              {idx + 1}
                            </span>
                          </td>
                          <td style={{ padding: '6px 12px', fontSize: '13px', whiteSpace: 'nowrap' as const }}>
                            <span style={{ color: isDarkTheme ? '#e2e8f0' : '#334155', fontWeight: 500 }}>
                              {formatTime(halt.startTime)}
                            </span>
                            <span style={{ color: isDarkTheme ? '#64748b' : '#94a3b8', margin: '0 4px' }}>→</span>
                            <span style={{ color: isDarkTheme ? '#e2e8f0' : '#334155', fontWeight: 500 }}>
                              {formatTime(halt.endTime)}
                            </span>
                          </td>
                          <td style={{ padding: '6px 12px', fontSize: '13px' }}>
                            <span style={{ fontWeight: 600, color: isDarkTheme ? '#fbbf24' : '#92400e' }}>
                              {halt.durationFormatted}
                            </span>
                          </td>
                          <td style={{ padding: '6px 12px', fontSize: '12px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                              {halt.address && halt.address !== '—' && (
                                <span
                                  style={{
                                    color: isDarkTheme ? '#cbd5e1' : '#475569',
                                    fontWeight: 500,
                                    fontSize: '12px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap' as const,
                                  }}
                                  title={halt.address}
                                >
                                  {halt.address}
                                </span>
                              )}
                              <a
                                href={`https://www.google.com/maps?q=${halt.location.lat},${halt.location.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: isDarkTheme ? '#60a5fa' : '#2563eb',
                                  textDecoration: 'none',
                                  fontWeight: 500,
                                  fontSize: '11px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                  <circle cx="12" cy="10" r="3" />
                                </svg>
                                {halt.location.lat.toFixed(4)}, {halt.location.lng.toFixed(4)}
                              </a>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          {/* Section divider */}
          <div style={{
            padding: '8px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            backgroundColor: isDarkTheme ? '#0f172a' : '#f1f5f9',
            borderTop: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
            borderBottom: isDarkTheme ? '2px solid #334155' : '2px solid #e2e8f0',
          }}>
            <span style={{
              fontSize: '14px',
              fontWeight: 600,
              color: isDarkTheme ? '#e2e8f0' : '#1e293b',
            }}>
              Vehicle Route
            </span>
            <div style={{
              display: 'flex',
              gap: '14px',
              alignItems: 'center',
              flexWrap: 'wrap',
              fontSize: '12px',
              color: isDarkTheme ? '#94a3b8' : '#64748b',
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <img src={START_MARKER_URL} alt="start" style={{ width: '16px', height: '16px' }} />
                Start
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <img src={END_MARKER_URL} alt="end" style={{ width: '16px', height: '16px' }} />
                End
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{
                  width: '10px', height: '10px', borderRadius: '50%',
                  backgroundColor: '#f97316', border: '1.5px solid #fff',
                  display: 'inline-block', boxShadow: '0 0 0 1px #94a3b8',
                }} />
                Halt
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{
                  width: '20px', height: '3px', borderRadius: '2px',
                  backgroundColor: '#3b82f6',
                  display: 'inline-block',
                }} />
                Path
              </span>
            </div>
          </div>

          {/* Map section — bottom, min 50% */}
          <div style={{
            flex: 1,
            minHeight: '50%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}>
            {/* Map container */}
            <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
              {loadingPath && (
                <div style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: isDarkTheme ? 'rgba(15,23,42,0.85)' : 'rgba(255,255,255,0.85)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  fontSize: '14px',
                  color: isDarkTheme ? '#94a3b8' : '#64748b',
                }}>
                  Loading vehicle path...
                </div>
              )}
              {pathError && (
                <div className="error-message" style={{
                  position: 'absolute',
                  top: '8px', left: '8px', right: '8px',
                  zIndex: 10,
                  fontSize: '13px',
                  margin: 0,
                  padding: '8px 12px',
                }}>
                  {pathError}
                </div>
              )}
              <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HaltDetailDrawer;
